/**
 * Pieces the AI usage tabs share: the run statuses, the grouping dimensions
 * and how a group narrows the run list, the period (a month or a whole year)
 * and number and day formatting.
 */

import type { AiRunStatus, Period, RunGroupBy } from '@/api/types';
import { currentMonth, monthLabel, shiftMonth } from '@/ui/format';

import type { RunFilters } from './RunFilterBar';

export const STATUS_STYLE: Record<AiRunStatus, { className: string; label: string }> = {
  succeeded: { className: 'badge-success', label: 'Succeeded' },
  failed: { className: 'badge-danger', label: 'Failed' },
  refused: { className: 'badge-warning', label: 'Refused' },
  running: { className: 'badge-info', label: 'Running' },
  abandoned: { className: 'badge-neutral', label: 'Abandoned' },
};
export const STATUSES = Object.keys(STATUS_STYLE) as AiRunStatus[];

export const GROUP_BY: { key: RunGroupBy; label: string; column: string; noun: string }[] = [
  { key: 'process', label: 'Process', column: 'Process', noun: 'processes' },
  { key: 'model', label: 'Model', column: 'Model', noun: 'models' },
  { key: 'user', label: 'Who', column: 'Who', noun: 'people' },
  { key: 'day', label: 'Day', column: 'Day', noun: 'days' },
  // Only a year has more than one month.
  { key: 'month', label: 'Month', column: 'Month', noun: 'months' },
];
export const groupInfo = (by: RunGroupBy) => GROUP_BY.find((g) => g.key === by)!;

/**
 * The filter that narrows the run list to one group. Days are UTC dates (as
 * the server groups them), so a day sets the When filter in UTC.
 */
export function drillPatch(by: RunGroupBy, key: string): Partial<RunFilters> {
  if (by === 'process') return { processes: [key] };
  if (by === 'model') return { models: [key] };
  if (by === 'user') return { users: [key] };
  if (by === 'month') return { when: { kind: 'range', from: `${key}-01`, to: lastDayOf(key) }, whenUtc: true };
  return { when: { kind: 'day', day: key }, whenUtc: true };
}

// --- Periods: a month (YYYY-MM) or a whole year (YYYY) ----------------------------

export const isYear = (period: Period) => period.length === 4;

export function periodLabel(period: Period): string {
  return isYear(period) ? period : monthLabel(period);
}

/** The period `delta` months or years away, in the same kind. */
export function shiftPeriod(period: Period, delta: number): Period {
  return isYear(period) ? String(Number(period) + delta) : shiftMonth(period, delta);
}

/** Whether the period holds today (so nothing after it exists yet). */
export function isCurrentPeriod(period: Period): boolean {
  const now = currentMonth();
  return isYear(period) ? now.startsWith(period) : period === now;
}

/** The last day of a month (YYYY-MM) as YYYY-MM-DD. */
export function lastDayOf(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
}

/** Every day of a month (YYYY-MM) as YYYY-MM-DD. */
export function daysOf(month: string): string[] {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return Array.from({ length: new Date(y, m, 0).getDate() }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

/** The period's first day and its last pickable one (today, in the current period), as YYYY-MM-DD. */
export function periodDays(period: Period): { first: string; last: string } {
  const now = new Date();
  const today = `${currentMonth()}-${String(now.getDate()).padStart(2, '0')}`;
  const first = isYear(period) ? `${period}-01-01` : `${period}-01`;
  const end = isYear(period) ? `${period}-12-31` : lastDayOf(period);
  return { first, last: isCurrentPeriod(period) ? today : end };
}

/** A group's name as a link-styled button that opens the runs behind it. */
export function GroupName({ label, drillable, onClick }: { label: string; drillable: boolean; onClick: () => void }) {
  if (!drillable) return <>{label}</>;
  return (
    <button type="button" className="cell-link" title={`Show the runs for ${label}`} onClick={onClick}>
      {label}
    </button>
  );
}

/** 1.25 -> "1.3s", in the browser's number format. */
const seconds = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
export function formatSeconds(value: number): string {
  return `${seconds.format(value)}s`;
}

/** A day key (YYYY-MM-DD) as "Thu, Oct 1", read as a calendar day, not an instant. */
export function formatDay(key: string): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
